import React from "react"
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import { cn } from "@utils/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover"

import { ArrowRight } from "lucide-react"

interface HotSpots {
  title: string
  spots: {
    id: string
    label: string
    description: string
    position: {
      x: number // percentage
      y: number // percentage
    }
  }[]
}

interface HotspotsProps {
  hotspots: HotSpots
  imageData: IGatsbyImageData
}

const Hotspots: React.FC<HotspotsProps> = ({ hotspots, imageData }) => {
  const { title, spots } = hotspots
  const [selectedHotspotId, setSelectedHotspotId] = React.useState<
    string | null
  >(spots[0].id)

  const selectHotspot = (id: string) => {
    setSelectedHotspotId(id)
    // Additional logic to handle zoom can be added here
  }

  return (
    <section className="flex overflow-hidden bg-slate-100 py-5 max-lg:flex-col">
      <div className="basis-1/3 px-4 sm:px-6 lg:px-8">
        <h2 className="uppercase">{title}</h2>
        <ArrowRight className="size-10 rotate-90 stroke-fehr-500 lg:rotate-0" />
      </div>
      <div className="relative mx-auto h-[540px] w-[1000px] min-w-[1000px] p-5 max-lg:-translate-x-48">
        <GatsbyImage
          image={imageData}
          objectFit="contain"
          alt="Interactive Hotspots Image"
          className="!absolute inset-0 bottom-0 top-0"
        />
        {spots.map((hotspot, idx) => (
          <Popover key={idx}>
            <PopoverTrigger asChild>
              <button
                key={hotspot.id}
                aria-label={`Hotspot for ${hotspot.label}`}
                className={cn("group absolute rounded-full bg-black/50 p-3")}
                style={{
                  left: cn(`${hotspot.position.x}px`),
                  top: `${hotspot.position.y}px`,
                }}
                onClick={() => selectHotspot(hotspot.id)}
              >
                <span className="relative flex size-3 *:transition-all group-hover:scale-110">
                  <span
                    className={`absolute inline-flex h-full w-full rounded-full bg-fehr-50 opacity-90 ${selectedHotspotId === hotspot.id && "animate-ping"}`}
                  />
                  <span
                    className={`relative inline-flex size-3 rounded-full ring-2 ring-fehr-500 group-hover:bg-fehr-500 group-hover:ring-4 group-hover:ring-white ${selectedHotspotId === hotspot.id ? "bg-fehr-500" : "bg-white"}`}
                  />
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="bg-white text-center">
              <p>{hotspot.label}</p>
            </PopoverContent>
          </Popover>
        ))}
      </div>
    </section>
  )
}

export default Hotspots
